import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Network, CircleCheck, HelpCircle, FlaskConical, Clock } from "lucide-react";

const PLATFORM_META = {
  AMAZON: { label: "Amazon", tone: "text-amber-300" },
  TIKTOK: { label: "TikTok", tone: "text-emerald-300" },
  GOOGLE: { label: "Google", tone: "text-sky-300" },
  KICKSTARTER: { label: "Kickstarter", tone: "text-fuchsia-300" },
  SHOPIFY: { label: "Stores", tone: "text-zinc-400" },
  SUPPLIER: { label: "Suppliers", tone: "text-zinc-400" },
  META_AD: { label: "Ads", tone: "text-zinc-400" },
};

const STATUS_TEXT = {
  OBSERVED: "Observed", MOCK: "Mock evidence", OFFLINE_FIXTURE: "Offline fixture",
  NOT_OBSERVED: "Not observed", NOT_CHECKED: "Not checked", NO_EVIDENCE: "No evidence",
  NOT_RESEARCHED: "Not researched",
};

function OriginBadge({ origin }) {
  if (origin === "mock") return <span className="text-[9px] font-mono px-1 py-0.5 border border-amber-400/30 bg-amber-400/10 text-amber-300">MOCK</span>;
  if (origin === "offline_fixture") return <span className="text-[9px] font-mono px-1 py-0.5 border border-sky-400/30 bg-sky-400/10 text-sky-300">OFFLINE</span>;
  if (origin === "live") return <span className="text-[9px] font-mono px-1 py-0.5 border border-emerald-400/30 bg-emerald-400/10 text-emerald-300">LIVE</span>;
  return null;
}

function ProvenanceTag({ p }) {
  const tone = { OBSERVED: "text-emerald-400", CALCULATED: "text-sky-400", AI_INTERPRETATION: "text-violet-400" }[p] || "text-zinc-400";
  return <span className={`text-[9px] font-mono uppercase tracking-widest ${tone}`}>{p?.replace("_", " ")}</span>;
}

export default function CrossSourceGraph() {
  const [products, setProducts] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reviews, setReviews] = useState([]);

  const loadList = () => api.get("/graph/products").then((r) => setProducts(r.data.products));
  const loadReviews = () => api.get("/graph/review-queue").then((r) => setReviews(r.data.reviews));
  const loadDetail = () => selected && api.get(`/graph/product/${selected}`).then((r) => setDetail(r.data));
  useEffect(() => { loadList(); loadReviews(); }, []);
  useEffect(() => {
    if (selected) api.get(`/graph/product/${selected}`).then((r) => setDetail(r.data));
    else setDetail(null);
  }, [selected]);

  const resolve = async (review_id, decision) => {
    await api.post("/graph/resolve-review", { review_id, decision });
    toast.success(`Marked ${decision.replace("_", " ").toLowerCase()}`);
    loadReviews(); loadList();
  };
  const loadDemo = async () => {
    await api.post("/admin/graph/load-demo");
    toast.success("Offline demo loaded");
    loadList(); loadReviews();
  };

  if (!products) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading…</div>;

  return (
    <div className="p-8 max-w-[1200px]" data-testid="cross-source-graph">
      <div className="mb-1 flex items-center gap-2">
        <Network className="text-emerald-400" size={22} />
        <h1 className="font-head text-3xl font-bold tracking-tight">Product Evidence</h1>
      </div>
      <p className="text-[#71717A] text-sm mb-4 max-w-3xl">
        Everything we currently know about each product — demand signals, sellers, advertising, customers,
        suppliers, economics and risk — in one place. Sources not yet checked simply show "Not researched yet".
      </p>

      {products.length === 0 && (
        <div className="border border-dashed border-[#27272A] p-6 text-center mb-6" data-testid="graph-empty">
          <p className="text-sm text-[#A1A1AA] mb-1">No products yet.</p>
          <p className="text-[11px] text-[#52525B] font-mono mb-3">Real Amazon products populate automatically as the radar collects. The button below loads a demo dataset for testing only.</p>
          <button onClick={loadDemo} data-testid="graph-load-demo" className="text-[11px] font-mono px-3 py-1.5 border border-[#27272A] text-[#71717A] hover:bg-[#18181b]">
            Load offline demo (testing only)
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
        {/* product list + review queue */}
        <div className="space-y-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Products ({products.length})</div>
            <div className="space-y-1.5">
              {products.map((p) => (
                <button key={p.canonical_product_id} data-testid={`graph-product-${p.canonical_product_id}`}
                  onClick={() => setSelected(p.canonical_product_id)}
                  className={`w-full text-left border px-3 py-2.5 ${selected === p.canonical_product_id ? "border-emerald-400/40 bg-emerald-400/5" : "border-[#27272A] bg-[#101013] hover:border-[#3f3f46]"}`}>
                  <div className="font-head text-sm font-semibold capitalize">{p.display_name}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Object.entries(p.platforms).map(([plat, origin]) => (
                      <span key={plat} className={`text-[10px] font-mono ${PLATFORM_META[plat]?.tone || "text-zinc-400"}`}>
                        {PLATFORM_META[plat]?.label || plat}
                        {origin !== "live" ? ` (${origin === "mock" ? "mock" : "offline"})` : ""}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {reviews.length > 0 && (
            <div data-testid="graph-review-queue">
              <div className="text-[11px] uppercase tracking-widest text-amber-400/80 font-mono mb-2">Needs review ({reviews.length})</div>
              <div className="space-y-2">
                {reviews.map((r) => (
                  <div key={r.review_id} data-testid={`graph-review-${r.review_id}`} className="border border-amber-500/30 bg-amber-500/5 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Possible product match</div>
                    <div className="text-sm mb-1">Existing: <span className="font-head font-semibold capitalize">{r.candidate_display}</span></div>
                    <div className="text-xs text-[#A1A1AA] mb-2">
                      Incoming evidence — {PLATFORM_META[r.underlying_platform]?.label}: "{r.source_display_name}" <OriginBadge origin={r.data_origin} />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => resolve(r.review_id, "SAME_PRODUCT")} data-testid={`graph-same-${r.review_id}`} className="text-[11px] font-mono px-2.5 py-1 border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20">Same product</button>
                      <button onClick={() => resolve(r.review_id, "DIFFERENT_PRODUCT")} data-testid={`graph-diff-${r.review_id}`} className="text-[11px] font-mono px-2.5 py-1 border border-[#3f3f46] text-[#A1A1AA] hover:bg-[#18181B]">Different product</button>
                      <button onClick={() => resolve(r.review_id, "UNSURE")} className="text-[11px] font-mono px-2.5 py-1 border border-[#27272A] text-[#71717A] hover:bg-[#18181B]">Unsure</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* product detail */}
        <div>
          {!detail ? (
            <div className="border border-dashed border-[#27272A] p-8 text-center text-sm text-[#52525B] font-mono">
              Select a product to see its evidence sources and timeline.
            </div>
          ) : <ProductDetailView detail={detail} onReload={loadDetail} />}
        </div>
      </div>
    </div>
  );
}

function EconomicsSection({ productId }) {
  const [price, setPrice] = useState("");
  const [cac, setCac] = useState("");
  const [fixed, setFixed] = useState("");
  const [applied, setApplied] = useState(null);
  const [showAdv, setShowAdv] = useState(false);
  const [d, setD] = useState(null);
  const load = (sp, c, fx) => {
    const params = { product_id: productId };
    if (sp) params.selling_price = sp;
    if (c) params.assumed_cac = c;
    if (fx) params.fixed_cost = fx;
    api.get("/economics/product", { params }).then((r) => setD(r.data)).catch(() => setD({ state: "INCOMPLETE", missing: ["No supplier data"] }));
  };
  useEffect(() => { load(); }, [productId]);
  const apply = () => {
    const v = parseFloat(price); const c = parseFloat(cac); const fx = parseFloat(fixed);
    if (v > 0) setApplied(v);
    load(v > 0 ? v : null, c > 0 ? c : null, fx > 0 ? fx : null);
  };

  if (!d) return null;
  const sc = d.scenarios || {};
  const Money = ({ v }) => <span>{v == null ? "—" : `$${v}`}</span>;
  const sum = d.summary || {};
  return (
    <div data-testid="graph-economics">
      <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Can the numbers work?</div>
      {!sc.best ? (
        <div className="border border-dashed border-[#27272A] p-4 text-center text-xs text-[#52525B] font-mono">Need supplier prices first — check sourcing above.</div>
      ) : (
        <>
          {/* inputs */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <label className="text-xs text-[#A1A1AA]">Your target selling price
              <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={d.selling_price || "39"}
                data-testid="econ-price-input" className="block mt-1 bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs w-24 font-mono" /></label>
            <label className="text-xs text-[#A1A1AA]">What if ads cost this per customer?
              <input type="number" value={cac} onChange={(e) => setCac(e.target.value)} placeholder="e.g. 20"
                data-testid="econ-cac-input" className="block mt-1 bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs w-28 font-mono" /></label>
            <label className="text-xs text-[#A1A1AA]">One-time startup cost (optional)
              <input type="number" value={fixed} onChange={(e) => setFixed(e.target.value)} placeholder="e.g. 3000"
                data-testid="econ-fixed-input" className="block mt-1 bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs w-28 font-mono" /></label>
            <button onClick={apply} data-testid="econ-apply" className="text-xs font-mono px-3 py-1.5 border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20">Run the numbers</button>
            {d.selling_price && !applied && <span className="text-[10px] text-[#71717A] font-mono">using competitor median ${d.selling_price} (estimate)</span>}
          </div>

          {/* top summary */}
          <div className="border border-emerald-400/20 bg-emerald-400/[0.03] p-3 mb-3" data-testid="econ-summary">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 font-mono mb-2">In plain terms</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
              <div><div className="text-[#71717A]">Selling price</div><div className="text-[#E4E4E7] font-mono"><Money v={sum.selling_price} /></div></div>
              <div><div className="text-[#71717A]">Modeled cost to land a unit</div><div className="text-[#E4E4E7] font-mono"><Money v={sum.modeled_landed_cost} /></div></div>
              <div><div className="text-[#71717A]">Money left before advertising</div><div className="text-emerald-300 font-mono"><Money v={sum.money_before_ads} /></div></div>
              <div><div className="text-[#71717A]">Most you can pay per sale on ads</div><div className="text-sky-300 font-mono"><Money v={sum.breakeven_ad_ceiling} /></div></div>
              <div><div className="text-[#71717A]">Least cash to place an order</div><div className="text-[#E4E4E7] font-mono"><Money v={sum.lowest_inventory_cash} /></div></div>
              <div><div className="text-[#71717A]">Biggest unknown</div><div className="text-amber-300">{sum.biggest_uncertainty}</div></div>
            </div>
          </div>

          {/* advertising-cost explorer */}
          {d.cac_scenarios && d.cac_scenarios.length > 0 && (
            <div className="border border-[#27272A] p-3 mb-3" data-testid="econ-cac">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">What if ads cost X per customer?</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {d.cac_scenarios.map((c, i) => (
                  <div key={i} className={`border p-2 ${c.loses_money ? "border-red-400/30 bg-red-400/[0.04]" : "border-[#27272A] bg-[#101013]"}`} data-testid={`econ-cac-${c.assumed_cac}`}>
                    <div className="text-xs text-[#A1A1AA]">Ads cost ${c.assumed_cac}/sale</div>
                    <div className={`text-sm font-mono mt-1 ${c.loses_money ? "text-red-300" : "text-emerald-300"}`}>{c.loses_money ? "Loses " : "Keeps "}<Money v={Math.abs(c.remaining_per_order)} /> /order</div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-[#52525B] font-mono mt-2">Ad cost per customer is your assumption — it is never taken from observed ads.</div>
            </div>
          )}

          {/* cash by quantity + capital risk */}
          {d.cash_by_quantity && d.cash_by_quantity.length > 0 && (
            <div className="border border-[#27272A] p-3 mb-3" data-testid="econ-cash">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">How much cash would we need?</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[460px]">
                  <thead><tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                    <th className="text-left px-2 py-1">Supplier</th><th className="text-right px-2 py-1">Order size</th>
                    <th className="text-right px-2 py-1">Cost each</th><th className="text-right px-2 py-1">Cash for inventory</th>
                  </tr></thead>
                  <tbody>
                    {d.cash_by_quantity.map((r, i) => (
                      <tr key={i} className="border-b border-[#1c1c1f]">
                        <td className="px-2 py-1">{r.supplier}</td><td className="px-2 py-1 text-right font-mono">{r.quantity}</td>
                        <td className="px-2 py-1 text-right font-mono">${r.unit_price}</td><td className="px-2 py-1 text-right font-mono text-[#E4E4E7]">${r.inventory_cash}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-[#52525B] font-mono mt-1">Inventory purchase only — freight, duties and marketing are extra and not yet known (not counted as $0).</div>
              {d.capital_risk && d.capital_risk.lowest_cash_option && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-testid="econ-capital-risk">
                  <div className="border border-[#27272A] bg-[#101013] p-2">
                    <div className="text-[10px] text-[#71717A] font-mono">Lowest cash to start</div>
                    <div className="text-xs text-[#E4E4E7] mt-0.5">{d.capital_risk.lowest_cash_option.supplier} · {d.capital_risk.lowest_cash_option.quantity} units · <Money v={d.capital_risk.lowest_cash_option.inventory_cash} /></div>
                  </div>
                  <div className="border border-[#27272A] bg-[#101013] p-2">
                    <div className="text-[10px] text-[#71717A] font-mono">Cheapest per unit</div>
                    <div className="text-xs text-[#E4E4E7] mt-0.5">{d.capital_risk.cheapest_unit_option.supplier} · {d.capital_risk.cheapest_unit_option.quantity} units · <Money v={d.capital_risk.cheapest_unit_option.inventory_cash} /></div>
                  </div>
                  <div className="sm:col-span-2 text-[10px] text-amber-300/80 font-mono">{d.capital_risk.note}</div>
                </div>
              )}
            </div>
          )}

          {/* startup cost recovery */}
          {d.startup_cost && (
            <div className="border border-[#27272A] p-3 mb-3" data-testid="econ-startup">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">One-time startup cost</div>
              <div className="text-xs text-[#A1A1AA]">Startup cost <span className="text-[#E4E4E7] font-mono"><Money v={d.startup_cost.fixed_total} /></span> (kept separate from per-unit costs)</div>
              {d.startup_cost.orders_to_recover ? (
                d.startup_cost.orders_to_recover.recoverable === false ? (
                  <div className="text-xs text-red-300 mt-1">{d.startup_cost.orders_to_recover.note}</div>
                ) : (
                  <div className="text-xs text-emerald-300 mt-1">About {d.startup_cost.orders_to_recover.orders_needed} orders would cover it at your assumed ad cost <span className="text-[#52525B]">(scenario math only — not a sales or time forecast)</span></div>
                )
              ) : <div className="text-[10px] text-[#71717A] font-mono mt-1">Enter an assumed ad cost above to see how many orders would cover it.</div>}
            </div>
          )}

          {/* scenario ranges */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3" data-testid="econ-scenarios">
            {["best", "typical", "worst"].map((k) => {
              const s = sc[k]; const m = s.margins;
              return (
                <div key={k} className="border border-[#27272A] bg-[#101013] p-3" data-testid={`econ-scenario-${k}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-1">{k === "best" ? "Best case" : k === "typical" ? "Typical" : "Worst case"}</div>
                  <div className="text-xs text-[#A1A1AA]">${s.unit_price} each at {s.quantity} units</div>
                  <div className="text-sm text-[#E4E4E7] mt-1">Landed cost <Money v={s.landed_cost} /></div>
                  {m ? (
                    <>
                      <div className="text-sm mt-1">Gross margin <span className="text-emerald-300"><Money v={m.gross_margin[0]} /> ({m.gross_margin_pct[0]}%)</span></div>
                      <div className="text-xs text-[#A1A1AA] mt-1">Money before ads <Money v={m.contribution_margin_before_ads[0]} /></div>
                      <div className="text-[11px] text-sky-300 mt-0.5">Most you can pay per sale on ads <Money v={m.breakeven_cac[0]} /></div>
                    </>
                  ) : <div className="text-xs text-amber-300 mt-1">Enter a selling price to see margins</div>}
                </div>
              );
            })}
          </div>

          {/* standard vs improved */}
          {d.improved_vs_standard && (
            <div className="border border-[#27272A] p-3 mb-3" data-testid="econ-improved">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Standard version vs improved version</div>
              {d.improved_vs_standard.improvements.length === 0 ? (
                <div className="text-xs text-[#52525B]">No supplier-supported improvements identified yet.</div>
              ) : (
                <>
                  <div className="text-xs text-[#A1A1AA] mb-2">If we build: {d.improved_vs_standard.improvements.join(", ")} <span className="text-[#71717A]">(+<Money v={d.improved_vs_standard.extra_unit_cost} />/unit)</span></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border border-[#27272A] bg-[#101013] p-2"><div className="text-[10px] text-[#71717A] font-mono">Standard — money before ads</div><div className="text-sm text-[#E4E4E7] font-mono mt-0.5"><Money v={d.improved_vs_standard.standard_money_before_ads} /></div></div>
                    <div className="border border-[#27272A] bg-[#101013] p-2"><div className="text-[10px] text-[#71717A] font-mono">Improved — money before ads</div><div className="text-sm text-emerald-300 font-mono mt-0.5"><Money v={d.improved_vs_standard.improved_money_before_ads} /></div></div>
                  </div>
                  {(d.improved_vs_standard.warnings || []).map((w, i) => (
                    <div key={i} className="text-[11px] text-amber-300 mt-2" data-testid="econ-praise-warning">⚠ {w}</div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* bundle economics */}
          {d.bundle_economics && d.bundle_economics.length > 0 && (
            <div className="border border-[#27272A] p-3 mb-3" data-testid="econ-bundles">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Selling in packs</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[520px]">
                  <thead><tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                    <th className="text-left px-2 py-1">Pack</th><th className="text-left px-2 py-1">Store</th>
                    <th className="text-right px-2 py-1">Order price</th><th className="text-right px-2 py-1">Effective /unit</th>
                    <th className="text-right px-2 py-1">Cost to fill order</th><th className="text-right px-2 py-1">Money before ads</th>
                  </tr></thead>
                  <tbody>
                    {d.bundle_economics.map((b, i) => (
                      <tr key={i} className="border-b border-[#1c1c1f]">
                        <td className="px-2 py-1">{b.bundle}</td><td className="px-2 py-1 text-[#A1A1AA]">{b.store}</td>
                        <td className="px-2 py-1 text-right font-mono">${b.order_price}</td><td className="px-2 py-1 text-right font-mono">${b.effective_price_per_unit}</td>
                        <td className="px-2 py-1 text-right font-mono">${b.order_landed_cost}</td><td className="px-2 py-1 text-right font-mono text-emerald-300">${b.money_before_ads_per_order}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* sensitivity */}
          {d.sensitivity && d.sensitivity.length > 0 && (
            <div className="border border-[#27272A] p-3 mb-3" data-testid="econ-sensitivity">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">What changes the numbers the most?</div>
              {d.sensitivity.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-0.5 border-b border-[#1c1c1f] last:border-0">
                  <span className="text-[#A1A1AA]">{r.change}</span>
                  <span className="font-mono text-red-300">{r.money_before_ads_delta} /order</span>
                </div>
              ))}
            </div>
          )}

          {/* advanced details */}
          <button onClick={() => setShowAdv(!showAdv)} data-testid="econ-advanced-toggle" className="text-[10px] font-mono text-[#52525B] hover:text-[#A1A1AA] mb-1">{showAdv ? "▾ Hide" : "▸ Show"} advanced details</button>
          {showAdv && (
            <div className="border border-[#27272A] bg-[#0c0c0e] p-3 mb-2" data-testid="econ-advanced">
              {d.price_point_explorer && (
                <div className="overflow-x-auto mb-2">
                  <table className="w-full text-xs min-w-[560px]">
                    <thead><tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                      <th className="text-left px-2 py-1.5">Supplier</th><th className="text-right px-2 py-1.5">Quantity</th>
                      <th className="text-right px-2 py-1.5">Unit</th><th className="text-right px-2 py-1.5">Landed</th>
                      <th className="text-right px-2 py-1.5">Gross margin</th><th className="text-right px-2 py-1.5">Before ads</th>
                    </tr></thead>
                    <tbody>
                      {d.price_point_explorer.rows.map((r, i) => (
                        <tr key={i} className="border-b border-[#1c1c1f]">
                          <td className="px-2 py-1.5">{r.supplier}</td><td className="px-2 py-1.5 text-right font-mono">{r.quantity}</td>
                          <td className="px-2 py-1.5 text-right font-mono">${r.unit_price}</td><td className="px-2 py-1.5 text-right font-mono">${r.landed_cost}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-emerald-300">${r.gross_margin} ({r.gross_margin_pct}%)</td>
                          <td className="px-2 py-1.5 text-right font-mono">${r.contribution_before_ads}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="text-[10px] text-[#52525B] font-mono">Provenance: selling price = your assumption; landed cost & margins = calculated; shipping/duties/fees/fulfillment/returns = estimated defaults. Version {d.version}.</div>
            </div>
          )}

          <div className="text-[10px] text-[#52525B] font-mono">
            {d.assumptions_note} {d.note} What we still don't know: {(d.what_we_dont_know || []).join(", ")}.
          </div>
        </>
      )}
    </div>
  );
}

function SourceabilitySection({ productId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/sourceability/product", { params: { product_id: productId } }).then((r) => setD(r.data)).catch(() => setD({ sourceability: { status: "NOT_RESEARCHED" } })); }, [productId]);
  if (!d) return null;
  const s = d.sourceability || {};
  const brief = d.inquiry_brief || {};
  const STATUS = {
    NOT_RESEARCHED: "Suppliers not checked yet", UNKNOWN: "We don't know yet",
    GENERIC_SUPPLIER_OPTIONS_FOUND: "Standard versions available from suppliers",
    OEM_OPTIONS_FOUND: "Suppliers offering customization were found",
    CUSTOM_MANUFACTURING_REQUIRED: "Would probably need custom manufacturing",
    PROPRIETARY_OR_IP_REVIEW_REQUIRED: "Possible IP/proprietary — review needed",
    NOT_CURRENTLY_SOURCEABLE: "No suitable sourcing option found yet",
    INSUFFICIENT_SUPPLIER_RESEARCH: "Not enough supplier research yet",
  };
  const CONF = { HIGH: "Strong evidence this can be sourced", MEDIUM: "Moderate evidence this can be sourced",
    LOW: "Limited evidence so far", NONE: "Not enough evidence yet" };
  const SUPPORT = { SUPPORTS: ["✓ May support", "text-emerald-300"], CANNOT: ["✗ Cannot change", "text-rose-300"],
    NOT_CHECKED: ["? Not checked", "text-zinc-500"] };
  return (
    <div data-testid="graph-sourceability">
      <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Can we source this product?</div>
      {s.status === "NOT_RESEARCHED" ? (
        <div className="border border-dashed border-[#27272A] p-4 text-center text-xs text-[#52525B] font-mono">Suppliers not checked yet.</div>
      ) : (
        <>
          <div className="border border-[#27272A] bg-[#101013] p-4 mb-3">
            <div className="text-sm font-head font-semibold text-emerald-300" data-testid="src-status">{STATUS[s.status] || s.status}</div>
            <div className="text-xs text-[#A1A1AA] mt-1">{CONF[s.confidence]} — {s.confidence_reason}</div>
            <div className="text-[11px] text-amber-300/70 mt-1">{s.supplier_reliability_note}</div>
            <div className="flex flex-wrap gap-2 mt-3 text-xs font-mono">
              <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{s.suppliers_observed} suppliers observed</span>
              <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{s.customizers_observed} offer customization</span>
              {s.min_order_range && <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">Minimum orders {s.min_order_range[0]}–{s.min_order_range[1]} units</span>}
              {s.price_range && <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">Prices ${s.price_range[0]}–${s.price_range[1]} depending on quantity</span>}
            </div>
          </div>

          {/* improvement bridge */}
          {(s.improvement_bridge || []).length > 0 && (
            <div className="mb-3" data-testid="src-bridge">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-400/70 font-mono mb-1.5">Can suppliers make the improvements customers want?</div>
              <div className="space-y-2">
                {s.improvement_bridge.map((b) => (
                  <div key={b.capability} className="border border-[#27272A] bg-[#101013] px-3 py-2 text-xs">
                    <div className="font-head font-semibold">{b.improvement} <span className="text-[#71717A] font-normal">— {b.suppliers_may_support} supplier(s) may support</span></div>
                    <div className="text-[#52525B] mb-1">{b.why}</div>
                    <div className="flex flex-wrap gap-2">
                      {b.suppliers.map((sup, i) => (
                        <span key={i} className={`font-mono ${SUPPORT[sup.support][1]}`}>{sup.supplier_name}: {SUPPORT[sup.support][0]}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* supplier table with quantity-context pricing */}
          <div className="border border-[#27272A] overflow-x-auto mb-3">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                  <th className="text-left px-2 py-1.5">Supplier</th><th className="text-left px-2 py-1.5">Location</th>
                  <th className="text-left px-2 py-1.5">Minimum order</th><th className="text-left px-2 py-1.5">Price (by quantity)</th>
                  <th className="text-left px-2 py-1.5">Can customize?</th><th className="text-left px-2 py-1.5">Production</th>
                </tr>
              </thead>
              <tbody>
                {(s.suppliers || []).map((sup) => (
                  <tr key={sup.supplier_id} data-testid={`src-supplier-${sup.supplier_id}`} className="border-b border-[#1c1c1f]">
                    <td className="px-2 py-1.5">{sup.supplier_name}</td>
                    <td className="px-2 py-1.5 font-mono text-[#71717A]">{sup.country}</td>
                    <td className="px-2 py-1.5">{sup.min_order} units</td>
                    <td className="px-2 py-1.5">{sup.price_tiers.map((t, i) => <div key={i}>${t.unit_price} each at {t.quantity} units</div>)}</td>
                    <td className="px-2 py-1.5">{sup.can_customize ? <span className="text-emerald-300">Yes — {sup.customizations.join(", ") || "some changes"}</span> : "No"}</td>
                    <td className="px-2 py-1.5 font-mono text-[#71717A]">{sup.production_time_days}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* inquiry brief */}
          <details className="border border-[#27272A] bg-[#0c0c0e] mb-3" data-testid="src-inquiry">
            <summary className="px-4 py-2.5 cursor-pointer text-xs font-mono text-[#A1A1AA]">Questions to ask suppliers (preparation only)</summary>
            <div className="px-4 pb-3 text-xs text-[#A1A1AA]">
              <div className="mb-1"><span className="text-[#71717A]">We want:</span> {(brief.we_want || []).join(", ")}</div>
              <ul className="list-disc list-inside space-y-0.5">{(brief.questions || []).map((q, i) => <li key={i}>{q}</li>)}</ul>
              <div className="text-[10px] text-[#52525B] mt-2">{brief.note}</div>
            </div>
          </details>

          <div className="text-[10px] text-[#52525B] font-mono">
            What we still don't know: {(s.what_we_dont_know || []).join(", ")}. Next step: {s.next_step}.
          </div>
        </>
      )}
    </div>
  );
}

function CustomerSection({ productId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/customers/product", { params: { product_id: productId } }).then((r) => setD(r.data)).catch(() => setD({ context: { state: "NOT_RESEARCHED" }, top_problems: [], praise: [], feature_requests: [], claim_reality_gaps: [], differentiation_seeds: [] })); }, [productId]);
  if (!d) return null;
  const ctx = d.context || {};
  const PATTERN_CLS = {
    MULTI_SOURCE_RECURRING_PATTERN: "text-rose-300 border-rose-400/40 bg-rose-500/10",
    RECURRING_PATTERN: "text-amber-300 border-amber-400/40 bg-amber-500/10",
    ISOLATED_COMPLAINT: "text-zinc-400 border-zinc-500/30 bg-zinc-500/10",
    INSUFFICIENT_EVIDENCE: "text-zinc-500 border-zinc-600/30",
  };
  const PATTERN_TEXT = {
    MULTI_SOURCE_RECURRING_PATTERN: "Repeated across multiple sources",
    RECURRING_PATTERN: "Repeated complaint", ISOLATED_COMPLAINT: "Single complaint so far",
    INSUFFICIENT_EVIDENCE: "Not enough evidence yet",
  };
  return (
    <div data-testid="graph-customers">
      <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Customer intelligence</div>
      {ctx.state === "NOT_RESEARCHED" ? (
        <div className="border border-dashed border-[#27272A] p-4 text-center text-xs text-[#52525B] font-mono">Not researched yet.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3 text-xs font-mono">
            <span className="px-2 py-1 border border-teal-400/30 bg-teal-400/10 text-teal-300" data-testid="cust-state">{ctx.state}</span>
            <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{ctx.unique_customer_signals} unique signals</span>
            <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{(ctx.unique_sources || []).length} sources</span>
          </div>

          {/* top problems */}
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-widest text-rose-400/70 font-mono mb-1.5">Top customer problems</div>
            <div className="space-y-1.5">
              {d.top_problems.map((p) => (
                <div key={p.theme} data-testid={`cust-problem-${p.theme}`} className="border border-[#27272A] bg-[#101013] px-3 py-2 flex flex-wrap items-center gap-3 text-xs">
                  <span className="font-head font-semibold">{p.theme.replace(/_/g, " ").toLowerCase()}</span>
                  <span className="text-[#A1A1AA]">{p.unique_complaints} unique complaints · {p.unique_sources} sources</span>
                  <span className={`ml-auto text-[10px] font-mono px-2 py-0.5 border ${PATTERN_CLS[p.pattern] || ""}`}>{PATTERN_TEXT[p.pattern] || p.pattern.replace(/_/g, " ").toLowerCase()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            {/* praise */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400/70 font-mono mb-1.5">What customers love (don't break this)</div>
              <ul className="text-xs text-[#A1A1AA] space-y-0.5 list-disc list-inside" data-testid="cust-praise">
                {d.praise.length ? d.praise.map((p) => <li key={p.theme}>{p.theme.replace(/_/g, " ").toLowerCase()} ({p.count})</li>) : <li>—</li>}
              </ul>
            </div>
            {/* feature requests */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-sky-400/70 font-mono mb-1.5">Feature requests</div>
              <ul className="text-xs text-[#A1A1AA] space-y-0.5 list-disc list-inside">
                {d.feature_requests.length ? d.feature_requests.map((r) => <li key={r.signal_id}>"{r.text}"</li>) : <li>—</li>}
              </ul>
            </div>
          </div>

          {/* claim vs reality */}
          {d.claim_reality_gaps.filter((g) => g.theme).length > 0 && (
            <div className="mb-3" data-testid="cust-claim-reality">
              <div className="text-[10px] uppercase tracking-widest text-amber-400/70 font-mono mb-1.5">Claim vs customer experience</div>
              <div className="space-y-1.5">
                {d.claim_reality_gaps.filter((g) => g.theme).map((g, i) => (
                  <div key={i} className="border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-mono px-1.5 py-0.5 border border-amber-400/40 text-amber-300">{g.state.replace(/_/g, " ")}</span>
                      <span className="text-[#71717A]">{g.theme.toLowerCase()}</span>
                    </div>
                    <div className="text-[#A1A1AA] mt-1"><span className="text-violet-300">Advertiser:</span> "{g.claim}"</div>
                    <div className="text-[#A1A1AA]"><span className="text-teal-300">Customers:</span> {g.evidence}</div>
                    <div className="text-[10px] text-[#52525B] mt-0.5">{g.note}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* differentiation seeds */}
          {d.differentiation_seeds.length > 0 && (
            <div className="mb-3" data-testid="cust-diff-seeds">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-400/70 font-mono mb-1.5">Differentiation hypotheses (human-owned, not recommendations)</div>
              <ul className="text-xs text-[#A1A1AA] space-y-1">
                {d.differentiation_seeds.map((s, i) => (
                  <li key={i} className="border border-[#27272A] bg-[#101013] px-3 py-2">
                    <span className="text-[9px] font-mono px-1.5 py-0.5 border border-fuchsia-400/30 text-fuchsia-300 mr-2">RESEARCH HYPOTHESIS</span>
                    {s.hypothesis}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-[10px] text-[#52525B] font-mono">
            {ctx.bias_note} What we don't know: {(ctx.what_we_dont_know || []).join(", ")}.
          </div>
        </>
      )}
    </div>
  );
}

function AdsSection({ productId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/ads/product", { params: { product_id: productId } }).then((r) => setD(r.data)).catch(() => setD({ context: { state: "NOT_RESEARCHED" }, ads: [], creative_patterns: { themes: [] } })); }, [productId]);
  if (!d) return null;
  const ctx = d.context || {};
  const ads = d.ads || [];
  const themes = (d.creative_patterns || {}).themes || [];
  const STATE_LABEL = {
    NOT_RESEARCHED: "Not researched", INSUFFICIENT_COVERAGE: "Insufficient coverage",
    LIMITED_OBSERVED_ACTIVITY: "Limited observed activity",
    MULTIPLE_ADVERTISERS_OBSERVED: "Multiple advertisers observed",
    BROAD_OBSERVED_AD_ACTIVITY: "Broad observed ad activity",
  };
  return (
    <div data-testid="graph-ads">
      <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Ads &amp; creative activity</div>
      {ctx.state === "NOT_RESEARCHED" ? (
        <div className="border border-dashed border-[#27272A] p-4 text-center text-xs text-[#52525B] font-mono">Not researched yet.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3 text-xs font-mono">
            <span className="px-2 py-1 border border-fuchsia-400/30 bg-fuchsia-400/10 text-fuchsia-300" data-testid="ads-state">{STATE_LABEL[ctx.state] || ctx.state}</span>
            {Object.entries(ctx.platforms || {}).map(([p, v]) => (
              <span key={p} className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{p}: {v.advertisers_observed} advertisers · {v.distinct_creatives} creatives</span>
            ))}
            <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">Longest observed {ctx.cross_platform?.longest_observed_ad_days}d</span>
            <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{ctx.landing_pages_linked} landing pages linked to offers</span>
          </div>
          {themes.length > 0 && (
            <div className="text-xs text-[#A1A1AA] mb-3">
              <span className="text-[9px] font-mono uppercase tracking-widest text-violet-400 mr-2">AI interpretation</span>
              Observed creative themes: {themes.map((t) => `${t.angle.toLowerCase().replace("_", " ")} (${t.creatives})`).join(" · ")}
            </div>
          )}
          <div className="border border-[#27272A] overflow-x-auto mb-2">
            <table className="w-full text-xs min-w-[820px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                  <th className="text-left px-2 py-1.5">Advertiser</th><th className="text-left px-2 py-1.5">Platform</th>
                  <th className="text-left px-2 py-1.5">Hook</th><th className="text-left px-2 py-1.5">CTA</th>
                  <th className="text-left px-2 py-1.5">Observed</th><th className="text-left px-2 py-1.5">Landing</th>
                  <th className="text-left px-2 py-1.5">Offer</th><th className="text-left px-2 py-1.5">Source</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((a) => (
                  <tr key={a.ad_id} data-testid={`ad-row-${a.ad_id}`} className="border-b border-[#1c1c1f]">
                    <td className="px-2 py-1.5">{a.advertiser_name}</td>
                    <td className="px-2 py-1.5 font-mono text-[#71717A]">{a.platform}</td>
                    <td className="px-2 py-1.5 text-[#A1A1AA]">{a.hook || "—"}</td>
                    <td className="px-2 py-1.5">{a.cta || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[#71717A]">{a.longevity?.observed_days_by_radar}d · {a.longevity?.observation_count}x</td>
                    <td className="px-2 py-1.5">{a.landing_page_url ? <a href={a.landing_page_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline font-mono">page ↗</a> : "—"}</td>
                    <td className="px-2 py-1.5 font-mono">{a.linked_offer ? `${a.linked_offer.price} ${a.linked_offer.currency}` : "—"}</td>
                    <td className="px-2 py-1.5">{a.source_url ? <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline font-mono">ad ↗</a> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-[#52525B] font-mono">
            Observed sample — ad presence is not proof of profit/spend. What we don't know: {(ctx.what_we_dont_know || []).join(", ")}.
          </div>
        </>
      )}
    </div>
  );
}

function CompetitorSection({ productId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/competitors/product", { params: { product_id: productId } }).then((r) => setD(r.data)).catch(() => setD({ landscape: { state: "NOT_RESEARCHED" }, competitors: [] })); }, [productId]);
  if (!d) return null;
  const land = d.landscape || {};
  const obs = land.observed || {};
  const rows = d.competitors || [];
  const STATE_LABEL = {
    NOT_RESEARCHED: "Not researched", INSUFFICIENT_COVERAGE: "Insufficient coverage",
    LIMITED_OBSERVED_COMPETITION: "Limited observed competition",
    DEVELOPING_COMPETITION: "Developing competition", MANY_OBSERVED_COMPETITORS: "Many observed competitors",
  };
  return (
    <div data-testid="graph-competitors">
      <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Competitors &amp; stores</div>
      {land.state === "NOT_RESEARCHED" ? (
        <div className="border border-dashed border-[#27272A] p-4 text-center text-xs text-[#52525B] font-mono">Not researched yet.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3 text-xs font-mono">
            <span className="px-2 py-1 border border-sky-400/30 bg-sky-400/10 text-sky-300" data-testid="comp-state">{STATE_LABEL[land.state] || land.state}</span>
            <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{obs.dtc_stores_observed} DTC stores observed</span>
            <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{obs.marketplace_sellers_observed} marketplace sellers</span>
            {obs.price_range && <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">Observed price {obs.price_range[0]}–{obs.price_range[1]} {(obs.currencies || []).join("/")}</span>}
            <span className="px-2 py-1 border border-[#27272A] text-[#A1A1AA]">{obs.offering_free_shipping} free shipping · {obs.offering_guarantee} guarantee</span>
          </div>
          {(land.positioning_angles || []).length > 0 && (
            <div className="text-xs text-[#A1A1AA] mb-3">
              <span className="text-[9px] font-mono uppercase tracking-widest text-violet-400 mr-2">AI interpretation</span>
              Positioning: {land.positioning_angles.join(" · ")}
            </div>
          )}
          <div className="border border-[#27272A] overflow-x-auto mb-2">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                  <th className="text-left px-2 py-1.5">Store</th><th className="text-left px-2 py-1.5">Type</th>
                  <th className="text-right px-2 py-1.5">Price</th><th className="text-right px-2 py-1.5">Unit</th>
                  <th className="text-left px-2 py-1.5">Offer</th><th className="text-left px-2 py-1.5">Shipping</th>
                  <th className="text-left px-2 py-1.5">Positioning</th><th className="text-left px-2 py-1.5">Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.offer_id} data-testid={`comp-offer-${r.offer_id}`} className="border-b border-[#1c1c1f]">
                    <td className="px-2 py-1.5">{r.store_name}{r.match_status === "ENTITY_REVIEW" && <span className="ml-1 text-[9px] text-amber-300">(review)</span>}</td>
                    <td className="px-2 py-1.5 font-mono text-[#71717A]">{r.store_type}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.price != null ? `${r.price} ${r.currency}` : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-[#71717A]">{r.bundle_quantity > 1 && r.effective_unit_price != null ? `${r.effective_unit_price}/ea` : "—"}</td>
                    <td className="px-2 py-1.5">{r.discount || (r.compare_at_price ? `was ${r.compare_at_price}` : "—")}</td>
                    <td className="px-2 py-1.5">{r.free_shipping ? "Free" : "—"}{r.guarantee ? ` · ${r.guarantee}` : ""}</td>
                    <td className="px-2 py-1.5 text-[#A1A1AA]">{(r.positioning || []).join(", ") || "—"}</td>
                    <td className="px-2 py-1.5">{r.product_url ? <a href={r.product_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline font-mono">View ↗</a> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-[#52525B] font-mono">
            Calculated from observed sample — not a market total. What we don't know: {(land.what_we_dont_know || []).join(", ")}.
          </div>
        </>
      )}
    </div>
  );
}

function ProductDecisionSummary({ productId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/differentiation/summary", { params: { product_id: productId, selling_price: 39 } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [productId]);
  if (!d) return null;
  const Row = ({ label, children }) => (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 py-1 border-b border-[#1c1c1f] last:border-0">
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono sm:w-40 shrink-0">{label}</div>
      <div className="text-sm text-[#E4E4E7]">{children}</div>
    </div>
  );
  return (
    <div className="border border-sky-400/20 bg-sky-400/[0.03] p-4" data-testid="decision-summary">
      <div className="text-[11px] uppercase tracking-widest text-sky-300/80 font-mono mb-2">Product decision summary</div>
      <Row label="Why we're watching it">{d.momentum}</Row>
      <Row label="Competition">{d.competition.detail}</Row>
      <Row label="Main customer problem"><span className="capitalize">{d.main_customer_problem}</span></Row>
      <Row label="Can we source it?">{d.can_we_source_it.suppliers_observed} supplier option(s) observed</Row>
      <Row label="Can we improve it?">{d.can_we_improve_it}</Row>
      <Row label="Can the numbers work?">{d.can_the_numbers_work}</Row>
      {d.risk && <Row label="What could go wrong?"><span className={d.risk.blocking ? "text-rose-300" : "text-[#E4E4E7]"}>{d.risk.headline}</span></Row>}
      <Row label="Biggest unknown"><span className="text-amber-300">{d.biggest_unknown}</span></Row>
      <Row label="Next step">{d.next_step}</Row>
      <div className="text-[10px] text-[#52525B] font-mono mt-2">{d.note}</div>
    </div>
  );
}

const HUMAN_STATUS_PLAIN = {
  NOT_REVIEWED: "Not reviewed", WORTH_INVESTIGATING: "Worth investigating",
  NOT_INTERESTED: "Not interested", NEEDS_MORE_EVIDENCE: "Needs more evidence",
  REJECTED_BY_ANALYST: "Rejected",
};
const FEASIBILITY_PLAIN = {
  SUPPORTED: "Yes — supplier option found", PARTIALLY_SUPPORTED: "Possibly — needs confirmation",
  NOT_SUPPORTED: "Supplier says no", NOT_CHECKED: "Not checked yet", UNKNOWN: "We don't know",
};
const GAP_PLAIN = {
  COMMONLY_OFFERED: "Most competitors already have this", SOMETIMES_OFFERED: "Some competitors have this",
  RARELY_OBSERVED: "We rarely saw this", NOT_OBSERVED_IN_SAMPLE: "We didn't see this in the competitors we researched",
  NOT_RESEARCHED: "Competitors not checked yet",
};

function DifferentiationSection({ productId }) {
  const [d, setD] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [price, setPrice] = useState("39");
  const [vName, setVName] = useState("");
  const [vCaps, setVCaps] = useState([]);
  const [brief, setBrief] = useState(null);
  const [journal, setJournal] = useState([]);
  const [note, setNote] = useState("");

  const origin = productId;
  const loadDiff = () => api.get("/differentiation/product", { params: { product_id: productId } }).then((r) => setD(r.data)).catch(() => setD(null));
  const loadCmp = () => api.get("/differentiation/compare", { params: { product_id: productId, selling_price: parseFloat(price) || 39 } }).then((r) => setCmp(r.data)).catch(() => setCmp(null));
  const loadJournal = () => api.get("/differentiation/journal", { params: { product_id: productId } }).then((r) => setJournal(r.data.journal || [])).catch(() => {});
  useEffect(() => { loadDiff(); loadCmp(); loadJournal(); setBrief(null); }, [productId]);

  const toggleCap = (c) => setVCaps((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);
  const createVersion = async () => {
    if (!vName.trim()) { toast.error("Name the version"); return; }
    await api.post("/differentiation/versions", { product_id: productId, name: vName.trim(), improvements: vCaps, price_scenario: parseFloat(price) || null });
    toast.success("Version saved (kept in history)"); setVName(""); setVCaps([]); loadCmp();
  };
  const setStatus = async (cap, status) => {
    await api.post("/differentiation/hypothesis-status", { product_id: productId, capability: cap, status });
    toast.success("Saved"); loadDiff();
  };
  const openBrief = async (version_id) => {
    const r = await api.get("/differentiation/brief", { params: { product_id: productId, version_id, selling_price: parseFloat(price) || 39 } });
    setBrief(r.data);
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post("/differentiation/journal", { product_id: productId, entry: note.trim(), kind: "WHY_I_LIKE" });
    setNote(""); loadJournal(); toast.success("Noted");
  };

  if (!d) return null;
  const Money = ({ v }) => <span>{v == null ? "—" : `$${v}`}</span>;
  return (
    <div data-testid="graph-differentiation" className="space-y-4">
      <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono">How could we make this product better?</div>

      {/* problems + love side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="border border-[#27272A] bg-[#101013] p-3" data-testid="diff-problems">
          <div className="text-[10px] uppercase tracking-widest text-rose-300/80 font-mono mb-2">Problems worth solving</div>
          {d.problems_worth_solving.length === 0 ? <div className="text-xs text-[#52525B]">No strong recurring problem found yet.</div> :
            d.problems_worth_solving.map((p, i) => (
              <div key={i} className="text-xs text-[#E4E4E7] capitalize mb-1">{p.label} <span className="text-[#71717A] normal-case">— {p.unique_complaints} complaints across {p.unique_sources} source(s)</span></div>
            ))}
        </div>
        <div className="border border-emerald-400/20 bg-emerald-400/[0.03] p-3" data-testid="diff-love">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 font-mono mb-2">What customers already value <span className="text-emerald-300/60">(don't ruin these)</span></div>
          {d.things_customers_love.length === 0 ? <div className="text-xs text-[#52525B]">Nothing recorded yet.</div> :
            d.things_customers_love.map((p, i) => (
              <div key={i} className="text-xs text-[#E4E4E7] capitalize mb-1">{p.label}</div>
            ))}
        </div>
      </div>

      {/* possible improvements */}
      <div className="border border-[#27272A] p-3" data-testid="diff-improvements">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Possible improvements</div>
        {d.possible_improvements.length === 0 ? <div className="text-xs text-[#52525B]">No evidence-backed improvement yet — we don't invent changes without customer evidence.</div> :
          d.possible_improvements.map((im, i) => (
            <div key={i} className="border-b border-[#1c1c1f] last:border-0 py-2" data-testid={`diff-imp-${im.capability}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-[#E4E4E7]">{im.title}</span>
                <span className="text-[9px] font-mono px-1 py-0.5 border border-[#3f3f46] text-[#71717A]">{im.category.replace(/_/g, " ").toLowerCase()}</span>
                <span className="text-[10px] text-[#71717A] font-mono">confidence: {im.evidence_confidence.toLowerCase()}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-[11px] mt-1.5">
                <div><span className="text-[#52525B]">Can suppliers make it?</span><div className="text-[#A1A1AA]">{FEASIBILITY_PLAIN[im.supplier_feasibility.state]}</div></div>
                <div><span className="text-[#52525B]">What would it cost?</span><div className="text-[#A1A1AA]">{im.added_unit_cost != null ? `+$${im.added_unit_cost}/unit` : "Not known yet"}</div></div>
                <div><span className="text-[#52525B]">Competitors</span><div className="text-[#A1A1AA]">{GAP_PLAIN[im.competitor_gap.state]}</div></div>
                <div><span className="text-[#52525B]">Effect on economics</span><div className="text-[#A1A1AA]">{im.economic_impact}</div></div>
              </div>
              {im.trade_off && <div className="text-[11px] text-amber-300 mt-1" data-testid={`diff-tradeoff-${im.capability}`}>⚠ Trade-off to investigate: {im.trade_off}</div>}
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <span className="text-[10px] text-[#52525B] font-mono">Your call:</span>
                {["WORTH_INVESTIGATING", "NEEDS_MORE_EVIDENCE", "NOT_INTERESTED"].map((s) => (
                  <button key={s} onClick={() => setStatus(im.capability, s)} data-testid={`diff-status-${im.capability}-${s}`}
                    className={`text-[10px] font-mono px-2 py-0.5 border ${im.human_status === s ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-[#3f3f46] text-[#71717A] hover:text-[#A1A1AA]"}`}>
                    {HUMAN_STATUS_PLAIN[s]}
                  </button>
                ))}
                {im.human_status !== "NOT_REVIEWED" && <span className="text-[10px] text-emerald-300/70 font-mono">✓ {HUMAN_STATUS_PLAIN[im.human_status]}</span>}
              </div>
            </div>
          ))}
      </div>

      {/* market gaps + other differentiation ideas (collapsible to keep the page short) */}
      <details className="border border-[#27272A]">
        <summary className="cursor-pointer px-3 py-2 text-[10px] uppercase tracking-widest text-[#71717A] font-mono">Other angles to investigate (gaps · bundles · positioning · offers · experience)</summary>
        <div className="px-3 pb-3 space-y-2 text-xs" data-testid="diff-other-angles">
          {d.market_gaps.map((g, i) => <div key={`g${i}`} className="text-[#A1A1AA]"><span className="text-sky-300">Possible market gap:</span> {g.gap} <span className="text-[#71717A]">→ {g.research_direction}</span></div>)}
          {d.bundle_ideas.map((b, i) => <div key={`b${i}`} className="text-[#A1A1AA]"><span className="text-fuchsia-300">Bundle idea:</span> {b.idea} <span className="text-[#71717A]">— {b.why}</span></div>)}
          {d.positioning_ideas.map((p, i) => <div key={`p${i}`} className="text-[#A1A1AA]"><span className="text-amber-300">Positioning idea to test:</span> {p.idea} <span className="text-[#71717A]">— {p.why}</span></div>)}
          {d.offer_ideas.map((o, i) => <div key={`o${i}`} className="text-[#A1A1AA]"><span className="text-emerald-300">Offer idea:</span> {o.idea} <span className="text-[#71717A]">— {o.why}</span></div>)}
          {d.experience_ideas.map((e, i) => <div key={`e${i}`} className="text-[#A1A1AA]"><span className="text-zinc-300">Experience idea:</span> {e.idea} <span className="text-[#71717A]">— {e.why}</span></div>)}
          {(d.market_gaps.length + d.bundle_ideas.length + d.positioning_ideas.length + d.offer_ideas.length + d.experience_ideas.length) === 0 && <div className="text-[#52525B]">No additional angles found from current evidence.</div>}
        </div>
      </details>

      {/* build & compare versions */}
      <div className="border border-[#27272A] p-3" data-testid="diff-versions">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono">Compare product versions</div>
          <label className="text-[11px] text-[#A1A1AA] ml-auto">Price scenario
            <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} onBlur={loadCmp}
              data-testid="diff-price" className="ml-1 bg-[#18181B] border border-[#3f3f46] px-2 py-0.5 text-[11px] w-20 font-mono" /></label>
        </div>

        {/* version builder */}
        <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-[#1c1c1f]">
          <input value={vName} onChange={(e) => setVName(e.target.value)} placeholder="Version name (e.g. Balanced improvement)"
            data-testid="diff-version-name" className="bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs w-56 font-mono" />
          {d.possible_improvements.map((im) => (
            <button key={im.capability} onClick={() => toggleCap(im.capability)} data-testid={`diff-cap-${im.capability}`}
              className={`text-[10px] font-mono px-2 py-1 border ${vCaps.includes(im.capability) ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-[#3f3f46] text-[#71717A]"}`}>
              {im.title}
            </button>
          ))}
          <button onClick={createVersion} data-testid="diff-create-version" className="text-xs font-mono px-3 py-1 border border-sky-400/30 bg-sky-400/10 text-sky-300 hover:bg-sky-400/20">Save version</button>
        </div>

        {/* comparison table */}
        {cmp && (
          <div className="overflow-x-auto" data-testid="diff-compare-table">
            <table className="w-full text-xs min-w-[720px]">
              <thead><tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                <th className="text-left px-2 py-1">Version</th><th className="text-left px-2 py-1">Problems addressed</th>
                <th className="text-left px-2 py-1">Supplier can make</th><th className="text-right px-2 py-1">Unit cost</th>
                <th className="text-right px-2 py-1">Money before ads</th><th className="text-right px-2 py-1">Cash for order</th>
                <th className="text-left px-2 py-1">Trade-off / risk</th><th className="px-2 py-1"></th>
              </tr></thead>
              <tbody>
                {cmp.rows.map((r, i) => (
                  <tr key={i} className="border-b border-[#1c1c1f]" data-testid={`diff-row-${i}`}>
                    <td className="px-2 py-1.5 text-[#E4E4E7]">{r.name}</td>
                    <td className="px-2 py-1.5 text-[#A1A1AA] capitalize">{r.problems_addressed.join(", ") || "—"}</td>
                    <td className="px-2 py-1.5 text-[#A1A1AA]">{r.supplier_can_make}</td>
                    <td className="px-2 py-1.5 text-right font-mono"><Money v={r.unit_cost} /></td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-300"><Money v={r.money_before_ads} /></td>
                    <td className="px-2 py-1.5 text-right font-mono"><Money v={r.cash_for_moq} /></td>
                    <td className="px-2 py-1.5 text-amber-300/90 text-[11px]">{r.complexity_risk ? "Complexity risk" : (r.trade_offs || []).length ? r.trade_offs.join("; ") : "none known"}</td>
                    <td className="px-2 py-1.5">{r.version_id && <button onClick={() => openBrief(r.version_id)} data-testid={`diff-brief-btn-${i}`} className="text-[10px] font-mono px-2 py-0.5 border border-[#3f3f46] text-[#71717A] hover:text-[#A1A1AA]">Brief</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] text-[#52525B] font-mono mt-1">{cmp.note}</div>
          </div>
        )}
      </div>

      {/* better product brief */}
      {brief && (
        <div className="border border-emerald-400/20 bg-emerald-400/[0.03] p-3" data-testid="diff-brief">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300/80 font-mono mb-2">Better product brief — {brief.concept_name}</div>
          <div className="text-xs space-y-1.5 text-[#A1A1AA]">
            <div><span className="text-[#71717A]">Why we're considering this:</span> {brief.why_considering.join(" ")}</div>
            <div><span className="text-[#71717A]">Keep these features:</span> <span className="capitalize">{brief.keep_these_features.join(", ") || "—"}</span></div>
            <div><span className="text-[#71717A]">Improvements to investigate:</span> {brief.improvements_to_investigate.join(", ") || "—"}</div>
            <div><span className="text-[#71717A]">Supplier feasibility:</span> {brief.supplier_feasibility}</div>
            <div><span className="text-[#71717A]">Cost estimate:</span> standard <Money v={brief.cost_estimate.standard_unit} />/unit → proposed <Money v={brief.cost_estimate.proposed_unit} />/unit{brief.cost_estimate.unknown_cost_items.length ? " (some costs unknown)" : ""}</div>
            <div><span className="text-[#71717A]">Economics at ${brief.economics.selling_price}:</span> money before ads <span className="text-emerald-300"><Money v={brief.economics.money_before_ads} /></span> — {brief.economics.impact}</div>
            {brief.possible_trade_offs.length > 0 && <div className="text-amber-300">Possible trade-offs: {brief.possible_trade_offs.join("; ")}</div>}
            <div><span className="text-[#71717A]">Questions still unanswered:</span> {brief.questions_unanswered.join(", ")}</div>
            <div className="text-emerald-300">Next action: {brief.next_action}</div>
          </div>
          <div className="text-[10px] text-[#52525B] font-mono mt-2">{brief.note}</div>
        </div>
      )}

      {/* decision journal */}
      <details className="border border-[#27272A]">
        <summary className="cursor-pointer px-3 py-2 text-[10px] uppercase tracking-widest text-[#71717A] font-mono">Decision journal</summary>
        <div className="px-3 pb-3" data-testid="diff-journal">
          <div className="flex gap-2 mb-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why I like / reject this, or what needs testing…"
              data-testid="diff-journal-input" className="flex-1 bg-[#18181B] border border-[#3f3f46] px-2 py-1 text-xs font-mono" />
            <button onClick={addNote} data-testid="diff-journal-add" className="text-xs font-mono px-3 py-1 border border-[#3f3f46] text-[#A1A1AA] hover:bg-[#18181B]">Add note</button>
          </div>
          {journal.length === 0 ? <div className="text-[11px] text-[#52525B] font-mono">No notes yet.</div> :
            journal.map((j, i) => (
              <div key={i} className="text-[11px] text-[#A1A1AA] py-0.5 border-b border-[#1c1c1f] last:border-0">
                <span className="text-[#52525B] font-mono">{(j.at || "").slice(0, 10)}</span> {j.entry}
              </div>
            ))}
        </div>
      </details>

      <div className="text-[10px] text-[#52525B] font-mono">{d.note} What we still don't know: {(d.what_we_dont_know || []).join(", ")}.</div>
    </div>
  );
}

const REQ_STATUS_PLAIN = {
  NOT_CHECKED: "Not checked", EVIDENCE_OBTAINED: "Evidence obtained", NEEDS_SPECIALIST: "Needs specialist",
  RESOLVED: "Resolved", CANNOT_RESOLVE: "Cannot resolve",
};
const SEV_CLS = {
  CRITICAL: "border-rose-400/40 bg-rose-500/[0.06]", HIGH: "border-rose-400/25 bg-rose-500/[0.04]",
  MEDIUM: "border-amber-400/25 bg-amber-500/[0.03]", LOW: "border-[#27272A] bg-[#101013]",
  INFO: "border-[#27272A] bg-[#101013]",
};

function RiskSection({ productId }) {
  const [d, setD] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [adv, setAdv] = useState(false);
  const load = () => api.get("/risk/product", { params: { product_id: productId } }).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => {
    load();
    api.get("/risk/versions", { params: { product_id: productId } }).then((r) => setCmp(r.data)).catch(() => setCmp(null));
  }, [productId]);
  const setReq = async (category, status) => {
    await api.post("/risk/requirement-status", { product_id: productId, category, status });
    toast.success("Saved"); load();
  };
  if (!d) return null;
  const important = d.risks.filter((r) => ["HIGH", "CRITICAL"].includes(r.severity));
  const smaller = d.risks.filter((r) => !["HIGH", "CRITICAL"].includes(r.severity));
  const Card = ({ r }) => (
    <div className={`border p-3 ${SEV_CLS[r.severity]}`} data-testid={`risk-card-${r.category}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[#E4E4E7]">{r.title}</span>
        <span className="text-[9px] font-mono px-1 py-0.5 border border-[#3f3f46] text-[#71717A]">{r.status_plain}</span>
        {r.blocker_class === "CAPITAL_BLOCKER" && <span className="text-[9px] font-mono px-1 py-0.5 border border-rose-400/40 bg-rose-500/15 text-rose-200" data-testid={`risk-blocker-${r.category}`}>Do not proceed</span>}
        {r.ai_suggested && <span className="text-[9px] font-mono text-violet-300">possible concern to review</span>}
      </div>
      <div className="text-xs text-[#A1A1AA] mt-1">{r.plain}</div>
      {r.version_added_note && <div className="text-[11px] text-amber-300 mt-1">↳ {r.version_added_note}</div>}
      <div className="text-[11px] text-[#71717A] mt-1"><span className="text-[#52525B]">Why it matters:</span> {r.impact_note}</div>
      <div className="text-[10px] text-[#52525B] font-mono mt-1">Because: {r.evidence[0]?.fact} ({r.evidence[0]?.source})</div>
      {r.mitigation_cost && <div className="text-[11px] text-sky-300 mt-1">Possible cost to address: {r.mitigation_cost.label} — ~${r.mitigation_cost.amount} {r.mitigation_cost.type === "PER_UNIT" ? "per unit" : "one-time"} (estimate)</div>}
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        <span className="text-[10px] text-[#52525B] font-mono">Check status:</span>
        {["NOT_CHECKED", "NEEDS_SPECIALIST", "RESOLVED"].map((s) => (
          <button key={s} onClick={() => setReq(r.category, s)} data-testid={`risk-req-${r.category}-${s}`}
            className={`text-[10px] font-mono px-2 py-0.5 border ${r.requirement.status === s ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-[#3f3f46] text-[#71717A] hover:text-[#A1A1AA]"}`}>
            {REQ_STATUS_PLAIN[s]}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div data-testid="graph-risk" className="space-y-4">
      <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono">What could go wrong?</div>

      {/* headline summary */}
      <div className={`border p-3 ${d.summary.blocking ? "border-rose-400/40 bg-rose-500/[0.06]" : "border-[#27272A] bg-[#101013]"}`} data-testid="risk-summary">
        <div className={`text-sm ${d.summary.blocking ? "text-rose-200" : "text-[#E4E4E7]"}`}>{d.summary.headline}</div>
        <div className="text-[11px] text-[#71717A] mt-1">Operational complexity: {d.operational_complexity.level}{d.operational_complexity.reasons.length ? ` — ${d.operational_complexity.reasons.join(", ")}` : ""}</div>
      </div>

      {/* capital blockers */}
      {d.capital_blockers.length > 0 && (
        <div className="border border-rose-400/40 bg-rose-500/[0.06] p-3" data-testid="risk-capital-blockers">
          <div className="text-[10px] uppercase tracking-widest text-rose-300 font-mono mb-1">Could stop us — resolve before spending money</div>
          {d.capital_blockers.map((r, i) => <div key={i} className="text-xs text-rose-200">{r.title} — {r.plain}</div>)}
        </div>
      )}

      {/* economics completeness warning */}
      {!d.economics_completeness.complete && (
        <div className="border border-amber-400/30 bg-amber-500/[0.04] p-3 text-xs text-amber-200" data-testid="risk-econ-warning">
          ⚠ {d.economics_completeness.warning}
        </div>
      )}

      {/* important risks */}
      {important.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="risk-important">{important.map((r) => <Card key={r.category} r={r} />)}</div>}
      {/* smaller concerns */}
      {smaller.length > 0 && (
        <details className="border border-[#27272A]">
          <summary className="cursor-pointer px-3 py-2 text-[10px] uppercase tracking-widest text-[#71717A] font-mono">Smaller / unchecked concerns ({smaller.length})</summary>
          <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="risk-smaller">{smaller.map((r) => <Card key={r.category} r={r} />)}</div>
        </details>
      )}

      {/* checklist */}
      <div className="border border-[#27272A] p-3" data-testid="risk-checklist">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono mb-2">What should we check before spending money?</div>
        <ul className="text-xs text-[#A1A1AA] space-y-1">
          {d.checklist.map((c, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={c.status === "RESOLVED" ? "text-emerald-400" : "text-[#52525B]"}>{c.status === "RESOLVED" ? "✓" : "○"}</span>
              <span>{c.check} <span className="text-[#52525B] font-mono">({REQ_STATUS_PLAIN[c.status]})</span></span>
            </li>
          ))}
        </ul>
      </div>

      {/* per-version risk comparison */}
      {cmp && cmp.rows.length > 1 && (
        <div className="border border-[#27272A] overflow-x-auto" data-testid="risk-versions">
          <table className="w-full text-xs min-w-[620px]">
            <thead><tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
              <th className="text-left px-2 py-1">Version</th><th className="text-right px-2 py-1">Important risks</th>
              <th className="text-left px-2 py-1">Specialist review</th><th className="text-left px-2 py-1">Blocking</th>
              <th className="text-left px-2 py-1">Operational</th><th className="text-left px-2 py-1">New risk from version</th>
            </tr></thead>
            <tbody>
              {cmp.rows.map((r, i) => (
                <tr key={i} className="border-b border-[#1c1c1f]">
                  <td className="px-2 py-1.5 text-[#E4E4E7]">{r.name}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.important_risks}</td>
                  <td className="px-2 py-1.5">{r.specialist_review ? "Yes" : "—"}</td>
                  <td className="px-2 py-1.5">{r.blocking ? <span className="text-rose-300">Yes</span> : "No"}</td>
                  <td className="px-2 py-1.5 text-[#A1A1AA]">{r.operational_complexity}</td>
                  <td className="px-2 py-1.5 text-amber-300/90 text-[11px]">{(r.new_from_version || []).join("; ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={() => setAdv(!adv)} data-testid="risk-advanced-toggle" className="text-[10px] font-mono text-[#52525B] hover:text-[#A1A1AA]">{adv ? "▾ Hide" : "▸ Show"} advanced details</button>
      {adv && (
        <div className="border border-[#27272A] bg-[#0c0c0e] p-3 text-[10px] font-mono text-[#52525B] space-y-1" data-testid="risk-advanced">
          {d.risks.map((r, i) => <div key={i}>{r.category} · sev {r.severity} · conf {r.confidence} · {r.status} · {r.blocker_class} · {r.provenance_chain.join(" → ")}</div>)}
        </div>
      )}
      <div className="text-[10px] text-[#52525B] font-mono">{d.note} What we still don't know: {(d.what_we_dont_know || []).join(", ")}.</div>
    </div>
  );
}

function ProductDetailView({ detail, onReload }) {
  const p = detail.product;
  const [platFilter, setPlatFilter] = useState("ALL");
  const [provFilter, setProvFilter] = useState("ALL");
  const [correcting, setCorrecting] = useState(null);
  const [newConcept, setNewConcept] = useState("");

  const events = detail.timeline.events.filter((e) =>
    (platFilter === "ALL" || e.platform === platFilter) &&
    (provFilter === "ALL" || e.provenance === provFilter));

  const submitCorrection = async (link_id) => {
    if (!newConcept.trim()) return;
    await api.post("/graph/correct-link", { link_id, new_concept: newConcept.trim(), reason: "analyst correction" });
    toast.success("Link corrected (old link preserved, new appended)");
    setCorrecting(null); setNewConcept(""); onReload && onReload();
  };
  return (
    <div data-testid="graph-product-detail" className="space-y-6">
      <div>
        <h2 className="font-head text-2xl font-bold capitalize">{p.display_name}</h2>
        <div className="text-[11px] font-mono text-[#52525B] mt-1">{p.canonical_product_id}</div>
        {p.aliases?.length > 0 && (
          <div className="text-xs text-[#71717A] mt-1">Also seen as: {p.aliases.join(" · ")}</div>
        )}
      </div>

      {/* product decision summary (Phase 9) */}
      <ProductDecisionSummary productId={p.canonical_product_id} />

      {/* source summary */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Evidence sources</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="graph-source-summary">
          {detail.source_summary.map((s) => (
            <div key={s.platform} data-testid={`graph-source-${s.platform}`} className="border border-[#27272A] bg-[#101013] p-3 flex items-center gap-3">
              <div className={`text-sm font-head font-semibold w-24 shrink-0 ${PLATFORM_META[s.platform]?.tone || "text-zinc-400"}`}>{PLATFORM_META[s.platform]?.label || s.platform}</div>
              <div className="flex-1">
                <div className="flex items-center gap-1.5 text-sm">
                  {s.has_evidence ? <CircleCheck size={13} className="text-emerald-400" /> : s.status === "NOT_CHECKED" || s.status === "NOT_RESEARCHED" ? <Clock size={13} className="text-[#52525B]" /> : <HelpCircle size={13} className="text-[#52525B]" />}
                  <span className={s.has_evidence ? "text-[#E4E4E7]" : "text-[#71717A]"}>{STATUS_TEXT[s.status] || s.status}</span>
                  <OriginBadge origin={s.data_origin} />
                </div>
                {s.detail && <div className="text-[11px] text-[#71717A] mt-0.5">{s.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* timeline */}
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono">Source timeline</div>
          <select value={platFilter} onChange={(e) => setPlatFilter(e.target.value)} data-testid="timeline-platform-filter"
            className="text-[11px] font-mono bg-[#101013] border border-[#27272A] px-2 py-1 text-[#A1A1AA]">
            {["ALL", "AMAZON", "TIKTOK", "GOOGLE", "KICKSTARTER"].map((x) => <option key={x} value={x}>{x === "ALL" ? "All platforms" : x}</option>)}
          </select>
          <select value={provFilter} onChange={(e) => setProvFilter(e.target.value)} data-testid="timeline-provenance-filter"
            className="text-[11px] font-mono bg-[#101013] border border-[#27272A] px-2 py-1 text-[#A1A1AA]">
            {["ALL", "OBSERVED", "CALCULATED", "AI_INTERPRETATION"].map((x) => <option key={x} value={x}>{x === "ALL" ? "All evidence types" : x.replace("_", " ")}</option>)}
          </select>
        </div>
        <div className="border-l border-[#27272A] pl-4 space-y-3" data-testid="graph-timeline">
          {events.length === 0 ? <div className="text-xs text-[#52525B] font-mono">No events match the filter.</div> : events.map((e, i) => (
            <div key={i} className="relative" data-testid={`graph-event-${e.type}`}>
              <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-[#3f3f46]" />
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-[11px] text-[#71717A] w-24 shrink-0">{(e.observed_at || "").slice(0, 10) || "—"}</span>
                <span className="text-sm text-[#E4E4E7]">{e.label}</span>
                <ProvenanceTag p={e.provenance} />
                <OriginBadge origin={e.data_origin} />
              </div>
              {e.detail && <div className="text-xs text-[#71717A] ml-[104px]">{e.detail}</div>}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#52525B] font-mono mt-3">
          "First observed by Breakout Radar" = when our collection first saw it — not necessarily when the product first existed.
        </p>
      </div>

      {/* entity links */}
      <details className="border border-[#27272A] bg-[#0c0c0e]" data-testid="graph-entity-links">
        <summary className="px-4 py-2.5 cursor-pointer text-xs font-mono text-[#A1A1AA]">Entity links ({detail.links.length}) — underlying source entities & audit</summary>
        <div className="px-4 pb-3 overflow-x-auto">
          <table className="w-full text-xs min-w-[640px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono border-b border-[#27272A]">
                <th className="text-left px-2 py-1.5">Platform</th><th className="text-left px-2 py-1.5">Entity</th>
                <th className="text-left px-2 py-1.5">Provider</th><th className="text-left px-2 py-1.5">Method</th>
                <th className="text-left px-2 py-1.5">Confidence</th><th className="text-left px-2 py-1.5">Human</th>
                <th className="text-left px-2 py-1.5">Correct</th>
              </tr>
            </thead>
            <tbody>
              {detail.links.map((l) => (
                <tr key={l.link_id} className="border-b border-[#1c1c1f]">
                  <td className="px-2 py-1.5 font-mono">{l.underlying_platform}</td>
                  <td className="px-2 py-1.5">{l.source_display_name} <span className="text-[#52525B] font-mono">({l.source_entity_id})</span></td>
                  <td className="px-2 py-1.5 font-mono text-[#71717A]">{l.provider || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[#71717A]">{l.match_method}</td>
                  <td className="px-2 py-1.5 font-mono">{l.match_confidence}</td>
                  <td className="px-2 py-1.5">{l.human_confirmed ? "✓" : "—"}</td>
                  <td className="px-2 py-1.5">
                    {correcting === l.link_id ? (
                      <span className="flex items-center gap-1">
                        <input value={newConcept} onChange={(e) => setNewConcept(e.target.value)} placeholder="new concept"
                          data-testid={`correct-input-${l.link_id}`} className="bg-[#18181B] border border-[#3f3f46] px-1.5 py-0.5 text-[11px] w-32" />
                        <button onClick={() => submitCorrection(l.link_id)} data-testid={`correct-save-${l.link_id}`} className="text-[10px] font-mono px-1.5 py-0.5 border border-emerald-400/30 text-emerald-300">Save</button>
                      </span>
                    ) : (
                      <button onClick={() => { setCorrecting(l.link_id); setNewConcept(""); }} data-testid={`correct-link-${l.link_id}`} className="text-[10px] font-mono px-1.5 py-0.5 border border-[#3f3f46] text-[#71717A] hover:text-[#A1A1AA]">Correct this link</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* competitors & stores */}
      <CompetitorSection productId={p.canonical_product_id} />

      {/* ads & creative activity */}
      <AdsSection productId={p.canonical_product_id} />

      {/* customer intelligence */}
      <CustomerSection productId={p.canonical_product_id} />

      {/* sourceability */}
      <SourceabilitySection productId={p.canonical_product_id} />

      {/* economics */}
      <EconomicsSection productId={p.canonical_product_id} />

      {/* differentiation / better product (Phase 9) */}
      <DifferentiationSection productId={p.canonical_product_id} />

      {/* risk / IP / compliance (Phase 10) */}
      <RiskSection productId={p.canonical_product_id} />

      {/* capital review entry (Phase 11) */}
      <a href={`/capital-review?product=${p.canonical_product_id}`} data-testid="open-capital-review"
        className="inline-flex items-center gap-2 text-sm font-mono px-4 py-2 border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20">
        Open Capital Review →
      </a>

      {/* next missing evidence — descriptive only */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#71717A] font-mono mb-2">Next missing evidence <span className="text-[#3f3f46]">(descriptive, not a priority score)</span></div>
        <ul className="text-xs text-[#A1A1AA] space-y-0.5 list-disc list-inside" data-testid="graph-missing">
          {detail.next_missing_evidence.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      </div>
    </div>
  );
}
